/*
 * waitlist.js — client behaviour for the waitlist form, mirroring the reference
 * site's UX: a pending "Submitting…" label, honeypot short-circuit, inputs that
 * clear on success, and a status line that fades in then auto-clears after 5s.
 *
 * Transport: POSTs JSON to /api/waitlist — our same-origin proxy that forwards to
 * the shared backend (see lib/waitlist-forward.js). The proxy returns
 * { status: 'success' | 'error', message }.
 */

const PENDING_LABEL = 'Submitting…';
const SUCCESS_FALLBACK = "Thanks. We'll be in touch.";
const ERROR_FALLBACK = 'Something went wrong. Please try again.';
const VALIDATION_ERROR = 'Please enter your name and a valid email.';
const STATUS_RESET_MS = 5000; // how long a resolved status stays visible
const CLEAR_INPUTS_MS = 200; // delay before wiping inputs on success (matches ref)

// Loose email sanity check — the form is novalidate, so we gate here instead of
// relying on the browser. Loops is the source of truth; this just catches obvious typos.
const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const form = document.querySelector('[data-waitlist-form]');
const statusEl = document.querySelector('[data-waitlist-status]');



if (form) {
  const submit = form.querySelector('.waitlist-submit');
  const defaultLabel = submit ? submit.textContent : 'Join the waitlist';
  let resetTimer = null;

  const setStatus = (message, isError) => {
    if (!statusEl) return;
    statusEl.textContent = message || '';
    statusEl.classList.toggle('error', Boolean(isError));
    statusEl.classList.toggle('active', Boolean(message));
  };

  const clearStatusLater = () => {
    clearTimeout(resetTimer);
    resetTimer = setTimeout(() => setStatus('', false), STATUS_RESET_MS);
  };

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (submit && submit.disabled) return; // ignore double-submits

    const data = Object.fromEntries(new FormData(form).entries());

    // Honeypot filled → a bot. Silently succeed without hitting the backend.
    if (data.website) {
      form.reset();
      setStatus(SUCCESS_FALLBACK, false);
      clearStatusLater();
      return;
    }

    // Client-side validation → error fades in under the button, no network call.
    const name = String(data.name || '').trim();
    const email = String(data.email || '').trim();
    if (!name || !isValidEmail(email)) {
      setStatus(VALIDATION_ERROR, true);
      clearStatusLater();
      return;
    }

    if (submit) {
      submit.disabled = true;
      submit.textContent = PENDING_LABEL;
    }
    setStatus('', false);

    let result;
    try {
      const res = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      result = await res.json();
    } catch {
      result = { status: 'error', message: ERROR_FALLBACK };
    }

    if (submit) {
      submit.disabled = false;
      submit.textContent = defaultLabel;
    }

    if (result && result.status === 'success') {
      setStatus(result.message || SUCCESS_FALLBACK, false);
      setTimeout(() => form.reset(), CLEAR_INPUTS_MS);
    } else {
      setStatus((result && result.message) || ERROR_FALLBACK, true);
    }
    clearStatusLater();
  });
}
